import DocumentMeta from 'react-document-meta';
import './tokens.css';
import styles from './login.module.css';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../header/index';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp, getApps, initializeApp } from 'firebase/app';
import DotLoading from './loading';
import ErrorMessage from '../../package/error/index';
import AppPreview from './items/appPreview';

// Firebase 初期化はコンポーネント外で
// この静的importはESモジュールの評価順の都合で src/index.js より先に実行され得るため、
// src/index.js と同じ条件分岐(E2E時はEmulator用の固定config)にして、
// どちらが先に initializeApp を呼んでも同一のconfigになるようにしている。
// (config不一致だと2回目の呼び出しで "app/duplicate-app" 例外が発生するため)
const useEmulator = process.env.REACT_APP_USE_EMULATOR === 'true';
const firebaseConfig = useEmulator
  ? {
      apiKey: 'demo-api-key',
      authDomain: 'localhost',
      projectId: 'demo-memorii-e2e',
      storageBucket: 'demo-memorii-e2e.appspot.com',
    }
  : {
      apiKey: process.env.REACT_APP_APIKEY,
      authDomain: process.env.REACT_APP_AUTHDOMAIN,
      databaseURL: process.env.REACT_APP_DATABASEURL,
      projectId: process.env.REACT_APP_PROJECT_ID,
      storageBucket: process.env.REACT_APP_STORAGE_BUCKET,
      messagingSenderId: process.env.REACT_APP_MESSAGING_SENDER_ID,
      appId: process.env.REACT_APP_APP_ID,
      measurementId: process.env.REACT_APP_MEASUREMENT_ID,
    };

const app = getApps().length ? getApp() : initializeApp(firebaseConfig);

type LoadingState = "idle" | "loading" | "success" | "error";

const AuthReset: React.FC = () => {

  const navigate = useNavigate();
  const functions = getFunctions(app, "asia-northeast1");

  const [ registerEmail, setRegisterEmail ] = useState('');
  const [ loading, setLoading ] = useState<LoadingState>("idle");
  const [ errorMessage, setErrorMessage ] = useState('');

  function check() {
    setErrorMessage('');
    if (registerEmail === '') {
      setErrorMessage('メールアドレスを入力してください。');
    } else {
      passwordReset();
    }
  }

  const passwordReset = async () => {
    setLoading("loading");
    try {
      const sendResetEmail = httpsCallable(functions, "sendPasswordResetEmail");
      await sendResetEmail({ email: registerEmail });
      alert("パスワードリセット用のメールを送信しました！");
      setLoading("success");
    } catch (error: any) {
      setLoading("error");
      alert("送信に失敗しました: " + error.message);
    }
  };

  const meta = {
    title: "パスワードリセット | 株式会社memorii（memorii works）",
    description: "株式会社memoriiが提供する『memorii works』のパスワードリセットページです。登録済みのメールアドレスからパスワードの再設定ができます。",
    canonical: "https://memorii-works.com/company/reset",
    meta: { charset: "utf-8", name: { robots: 'noindex,nofollow,noarchive,noimageindex' } }
  };

  return (
    <DocumentMeta {...meta}>
      <div className={styles.page}>

        <Header />

        <main className={styles.wrap}>
          <div className={styles.card}>

            <section className={styles.brand}>
              <div className={styles.brandInner}>
                <p className={styles.brandTagline}>
                  美容業界の新卒採用を、<br />
                  ひとつの仕組みに。
                </p>
                <AppPreview />
              </div>
            </section>

            <section className={styles.panel}>

              {loading === "loading" && (
                <div className={styles.state}>
                  <DotLoading text="処理中" />
                </div>
              )}

              {loading === "success" && (
                <div className={styles.state}>
                  <p className={styles.stateText}>
                    メールアドレスに届いたURLよりパスワードを変更してください。
                  </p>
                  <button type="button" className={styles.linkBtn} onClick={() => navigate('/company/login')}>
                    ログイン画面はこちら
                  </button>
                </div>
              )}

              {(loading === "idle" || loading === "error") && (
                <form
                  className={styles.form}
                  onSubmit={(e) => { e.preventDefault(); check(); }}
                >
                  <h1 className={styles.title}>パスワードリセット</h1>

                  {errorMessage && <ErrorMessage message={errorMessage} />}

                  <div className={styles.field}>
                    <label htmlFor="reset-email" className={styles.label}>メールアドレス</label>
                    <input
                      id="reset-email"
                      className={styles.input}
                      type="email"
                      autoComplete="email"
                      placeholder="memorii@trankllc.com"
                      value={registerEmail}
                      onChange={(e) => setRegisterEmail(e.target.value)}
                    />
                  </div>

                  <button type="submit" className={styles.submit}>リセット</button>

                  <div className={styles.links}>
                    <button type="button" className={styles.linkBtn} onClick={() => navigate('/company/signin')}>
                      アカウントをお持ちではない方はこちら
                    </button>
                    <button type="button" className={styles.linkBtn} onClick={() => navigate('/company/login')}>
                      アカウントをお持ちの方はこちら
                    </button>
                  </div>
                </form>
              )}

            </section>

          </div>
        </main>

      </div>
    </DocumentMeta>
  );
}

export default AuthReset;
